import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Shield,
  Brain,
  Lock,
  CheckCircle2,
  Info,
  X,
  ChevronDown,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export interface PetForConsent {
  id: string;
  name: string;
  did?: string;
  species?: string;
  breed?: string;
}

interface AIConsentFlowProps {
  pets: PetForConsent[];
  initialPetId?: string;
  onComplete: (settings: AIConsentSettings) => void;
  onClose: () => void;
}

export interface AIConsentSettings {
  aiEnabled: true;
  allowProfessionalView: boolean;
  activatedDate: string;
  selectedPetIds: string[];
}

export function AIConsentFlow({
  pets,
  initialPetId,
  onComplete,
  onClose,
}: AIConsentFlowProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'consent' | 'did-link' | 'sharing'>(
    'consent'
  );
  const [aiEnabled, setAiEnabled] = useState(false);
  const [allowProfessionalView, setAllowProfessionalView] = useState(true);
  const [selectedPetIds, setSelectedPetIds] = useState<string[]>([]);

  useEffect(() => {
    if (!pets || pets.length === 0) {
      setSelectedPetIds([]);
      return;
    }
    setSelectedPetIds((prev) => {
      const valid = prev.filter((id) => pets.some((p) => p.id === id));
      if (valid.length > 0) return valid;
      if (initialPetId && pets.some((p) => p.id === initialPetId)) {
        return [initialPetId];
      }
      return [pets[0].id];
    });
  }, [pets, initialPetId]);

  const selectedPets = pets.filter((p) => selectedPetIds.includes(p.id));
  const selectedNames =
    selectedPets.length === 0
      ? ''
      : selectedPets.length === 1
        ? selectedPets[0].name
        : selectedPets.length === 2
          ? `${selectedPets[0].name} and ${selectedPets[1].name}`
          : `${selectedPets.length} pets`;

  const togglePet = (petId: string) => {
    setSelectedPetIds((prev) =>
      prev.includes(petId)
        ? prev.filter((id) => id !== petId)
        : [...prev, petId]
    );
  };

  const selectAll = () => {
    if (selectedPetIds.length === pets.length) {
      setSelectedPetIds([]);
    } else {
      setSelectedPetIds(pets.map((p) => p.id));
    }
  };

  const allSelected = pets.length > 0 && selectedPetIds.length === pets.length;

  const handleActivate = () => {
    if (!aiEnabled || selectedPetIds.length === 0) return;

    onComplete({
      aiEnabled: true,
      allowProfessionalView,
      activatedDate: new Date().toISOString(),
      selectedPetIds,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border dark:border-white/8">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 dark:bg-white/10 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h2
                  className="text-xl"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.aiConsent.title', 'Activate AI Health Insights')}
                </h2>
                <p
                  className="text-blue-100 dark:text-blue-200 text-sm"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {selectedPetIds.length === 0
                    ? t(
                        'features.aiConsent.selectPetsBelow',
                        'Select pets below'
                      )
                    : selectedPetIds.length === 1
                      ? t('features.aiConsent.forPet', {
                          name: selectedPets[0]?.name || 'pet',
                        })
                      : t('features.aiConsent.forPetsCount', {
                          count: selectedPetIds.length,
                        })}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 dark:bg-white/10 dark:hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 px-6 py-4 bg-gray-50 dark:bg-[#0F0E16] border-b border-gray-200 dark:border-white/8">
          <div
            className={`flex items-center gap-2 ${step === 'consent' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'consent'
                  ? 'bg-blue-100 dark:bg-blue-900/30'
                  : 'bg-green-100 dark:bg-green-900/30'
              }`}
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {step === 'consent' ? '1' : <CheckCircle2 className="w-5 h-5" />}
            </div>
            <span
              className="text-sm"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('features.aiConsent.stepConsent', 'Consent')}
            </span>
          </div>
          <div
            className={`h-px w-12 ${step !== 'consent' ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-300 dark:bg-white/20'}`}
          />
          <div
            className={`flex items-center gap-2 ${step === 'did-link' ? 'text-blue-600 dark:text-blue-400' : step === 'sharing' ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-white/40'}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'did-link'
                  ? 'bg-blue-100 dark:bg-blue-900/30'
                  : step === 'sharing'
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-gray-100 dark:bg-white/10'
              }`}
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {step === 'sharing' ? <CheckCircle2 className="w-5 h-5" /> : '2'}
            </div>
            <span
              className="text-sm"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('features.aiConsent.stepDidLink', 'DID Link')}
            </span>
          </div>
          <div
            className={`h-px w-12 ${step === 'sharing' ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-300 dark:bg-white/20'}`}
          />
          <div
            className={`flex items-center gap-2 ${step === 'sharing' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-white/40'}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'sharing'
                  ? 'bg-blue-100 dark:bg-blue-900/30'
                  : 'bg-gray-100 dark:bg-white/10'
              }`}
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              3
            </div>
            <span
              className="text-sm"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('features.aiConsent.stepSharing', 'Sharing')}
            </span>
          </div>
        </div>

        {/* Step 1: Consent */}
        {step === 'consent' && (
          <div className="p-6 space-y-6 bg-white dark:bg-[#16151D]">
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-500/30">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-2">
                  <h3
                    className="text-blue-900 dark:text-blue-100"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    {t(
                      'features.aiConsent.whatAIDoes',
                      'What AI Health Insights Does'
                    )}
                  </h3>
                  <ul
                    className="text-sm text-blue-800 dark:text-blue-200 space-y-1.5"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 dark:text-blue-400 mt-1">
                        •
                      </span>
                      <span>{t('features.aiConsent.does1')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 dark:text-blue-400 mt-1">
                        •
                      </span>
                      <span>{t('features.aiConsent.does2')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 dark:text-blue-400 mt-1">
                        •
                      </span>
                      <span>{t('features.aiConsent.does3')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 dark:text-blue-400 mt-1">
                        •
                      </span>
                      <span>{t('features.aiConsent.does4')}</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-500/30">
                <Shield className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-2">
                  <h3
                    className="text-red-900 dark:text-red-100"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    {t(
                      'features.aiConsent.whatAIDoesNot',
                      'What AI Health Insights Does NOT Do'
                    )}
                  </h3>
                  <ul
                    className="text-sm text-red-800 dark:text-red-200 space-y-1.5"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 dark:text-red-400 mt-1">
                        •
                      </span>
                      <span>{t('features.aiConsent.notDoes1')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 dark:text-red-400 mt-1">
                        •
                      </span>
                      <span>{t('features.aiConsent.notDoes2')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 dark:text-red-400 mt-1">
                        •
                      </span>
                      <span>{t('features.aiConsent.notDoes3')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 dark:text-red-400 mt-1">
                        •
                      </span>
                      <span>{t('features.aiConsent.notDoes4')}</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-3">
                <h3
                  className="text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {t('features.aiConsent.dataSourcesUsed')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    {
                      icon: Brain,
                      label: t(
                        'features.aiConsent.petProfileData',
                        'Pet Profile Data'
                      ),
                    },
                    {
                      icon: Shield,
                      label: t(
                        'features.aiConsent.healthRecords',
                        'Health Records'
                      ),
                    },
                    {
                      icon: Sparkles,
                      label: t(
                        'features.aiConsent.vaccinationHistory',
                        'Vaccination History'
                      ),
                    },
                    {
                      icon: Lock,
                      label: t(
                        'features.aiConsent.groomingActivity',
                        'Grooming & Activity'
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-[#0F0E16] rounded-lg border border-gray-200 dark:border-white/8"
                    >
                      <item.icon className="w-4 h-4 text-gray-600 dark:text-white/70" />
                      <span
                        className="text-sm text-gray-700 dark:text-white/90"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-500/30 rounded-lg">
                <h3
                  className="text-purple-900 dark:text-purple-100 mb-2"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {t('features.aiConsent.privacyControlTitle')}
                </h3>
                <p
                  className="text-sm text-purple-800 dark:text-purple-200"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.aiConsent.privacyControlDesc')}
                </p>
              </div>

              {/* Pet multi-select dropdown */}
              <div className="space-y-3">
                <h3
                  className="text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {t(
                    'features.aiConsent.selectPets',
                    'Select Pet(s) for AI Consent'
                  )}
                </h3>
                <p
                  className="text-sm text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.aiConsent.choosePetsDesc')}
                </p>
                {!pets || pets.length === 0 ? (
                  <div
                    className="p-4 border-2 border-dashed border-gray-300 dark:border-white/20 rounded-lg text-sm text-gray-600 dark:text-white/60"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('features.aiConsent.noPetsFound')}
                  </div>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center justify-between w-full px-4 py-3 rounded-lg border-2 border-gray-200 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white hover:border-gray-300 dark:hover:border-white/30 transition-colors text-left"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <span>
                          {selectedPetIds.length === 0
                            ? t(
                                'features.aiConsent.selectPetsPlaceholder',
                                'Select pet(s)...'
                              )
                            : selectedPetIds.length === 1
                              ? selectedPets[0]?.name
                              : t('features.aiConsent.petsSelectedCount', {
                                  count: selectedPetIds.length,
                                })}
                        </span>
                        <ChevronDown className="w-5 h-5 text-gray-500 dark:text-white/50 flex-shrink-0 ml-2" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-[var(--radix-popover-trigger-width)] p-0"
                    >
                      <div className="max-h-64 overflow-y-auto p-2">
                        <label
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 transition-colors border-b border-gray-100 dark:border-white/10 mb-2"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={selectAll}
                            className="w-5 h-5 rounded border-gray-300 dark:border-white/20 text-blue-600"
                          />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {t('features.aiConsent.selectAll')}
                          </span>
                        </label>
                        {pets.map((p) => {
                          const emoji =
                            p.species === 'dog'
                              ? '🐕'
                              : p.species === 'cat'
                                ? '🐈'
                                : '🐾';
                          const isSelected = selectedPetIds.includes(p.id);
                          return (
                            <label
                              key={p.id}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => togglePet(p.id)}
                                className="w-5 h-5 rounded border-gray-300 dark:border-white/20 text-blue-600"
                              />
                              <span className="text-lg">{emoji}</span>
                              <span className="text-gray-900 dark:text-white">
                                {p.name}
                              </span>
                              {p.breed && (
                                <span className="text-xs text-gray-500 dark:text-white/50 ml-auto truncate max-w-[100px]">
                                  {p.breed}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-white/8">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={aiEnabled}
                  onChange={(e) => setAiEnabled(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 dark:border-white/20 text-blue-600 dark:text-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-[#0F0E16]"
                />
                <div>
                  <p
                    className="group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors text-gray-900 dark:text-white"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('features.aiConsent.iUnderstandEnable')}{' '}
                    {selectedNames || t('features.aiConsent.selectedPets')}
                  </p>
                  <p
                    className="text-sm text-gray-600 dark:text-white/70 mt-1"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('features.aiConsent.aiAnalyzeNotice')}
                  </p>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.aiConsent.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => setStep('did-link')}
                disabled={!aiEnabled || selectedPetIds.length === 0}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 dark:hover:from-blue-600 dark:hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.aiConsent.continue', 'Continue')}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: DID Link Confirmation */}
        {step === 'did-link' && (
          <div className="p-6 space-y-6 bg-white dark:bg-[#16151D]">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-2xl flex items-center justify-center mx-auto">
                <Shield className="w-10 h-10 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3
                  className="text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {t(
                    'features.aiConsent.didLinkedInsights',
                    'DID-Linked AI Insights'
                  )}
                </h3>
                <p
                  className="text-gray-600 dark:text-white/70 mt-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.aiConsent.didLinkedDesc')}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {selectedPets.map((p) => (
                <div
                  key={p.id}
                  className="p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg border border-gray-200 dark:border-white/8"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-sm text-gray-600 dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {p.name} – DID
                    </span>
                    {p.did && (
                      <span
                        className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        {t('features.aiConsent.verified')}
                      </span>
                    )}
                  </div>
                  <p className="font-mono text-sm text-gray-900 dark:text-white/90 break-all">
                    {p.did ||
                      t('features.aiConsent.noDidLinked', 'No DID linked')}
                  </p>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <h4
                className="text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t(
                  'features.aiConsent.benefitsTitle',
                  'Benefits of DID-Linked Insights'
                )}
              </h4>
              <div className="space-y-2">
                {[
                  t('features.aiConsent.benefit1'),
                  t('features.aiConsent.benefit2'),
                  t('features.aiConsent.benefit3'),
                  t('features.aiConsent.benefit4'),
                ].map((benefit) => (
                  <div key={benefit} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <p
                      className="text-sm text-gray-700 dark:text-white/90"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {benefit}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep('consent')}
                className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.aiConsent.back', 'Back')}
              </button>
              <button
                onClick={() => setStep('sharing')}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 dark:hover:from-blue-600 dark:hover:to-purple-600 transition-all"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.aiConsent.continue', 'Continue')}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Sharing Controls */}
        {step === 'sharing' && (
          <div className="p-6 space-y-6 bg-white dark:bg-[#16151D]">
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-2xl flex items-center justify-center mx-auto">
                  <Lock className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                </div>
                <h3
                  className="text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {t(
                    'features.aiConsent.controlWhoSeesInsights',
                    'Control Who Sees Insights'
                  )}
                </h3>
                <p
                  className="text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t(
                    'features.aiConsent.controlDesc',
                    'You have full control over insight sharing. You can change these settings anytime.'
                  )}
                </p>
              </div>

              <div className="space-y-3 pt-4">
                <div className="p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg border border-gray-200 dark:border-white/8">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4
                        className="text-gray-900 dark:text-white"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        {t(
                          'features.aiConsent.shareWithProfessionals',
                          'Share with Professionals'
                        )}
                      </h4>
                      <p
                        className="text-sm text-gray-600 dark:text-white/70 mt-1"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t(
                          'features.aiConsent.shareWithProfessionalsDesc',
                          'Allow veterinarians and other professionals to view AI insights during appointments'
                        )}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={allowProfessionalView}
                        onChange={(e) =>
                          setAllowProfessionalView(e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 dark:bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-white/20 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500"></div>
                    </label>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-500/30">
                  <h4
                    className="text-blue-900 dark:text-blue-100 mb-2"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    {t(
                      'features.aiConsent.sharingOptionsTitle',
                      'Sharing Options (can be changed later)'
                    )}
                  </h4>
                  <ul
                    className="text-sm text-blue-800 dark:text-blue-200 space-y-1.5"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <li>• {t('features.aiConsent.sharingOptionFull')}</li>
                    <li>• {t('features.aiConsent.sharingOptionSummary')}</li>
                    <li>• {t('features.aiConsent.sharingOptionZkp')}</li>
                    <li>• {t('features.aiConsent.sharingOptionNothing')}</li>
                  </ul>
                  <p
                    className="text-xs text-blue-700 dark:text-blue-300 mt-3"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('features.aiConsent.currentSelection')}{' '}
                    {allowProfessionalView
                      ? t('features.aiConsent.fullInsights', 'Full insights')
                      : t('features.aiConsent.nothing', 'Nothing')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep('did-link')}
                className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.aiConsent.back', 'Back')}
              </button>
              <button
                onClick={handleActivate}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 dark:hover:from-blue-600 dark:hover:to-purple-600 transition-all flex items-center justify-center gap-2"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                <Sparkles className="w-5 h-5" />
                {t(
                  'features.aiConsent.activateInsights',
                  'Activate AI Insights'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
